import { 
  Train,
  Bus,
  Ship,
  MapPin,
  Navigation,
  Users,
  Clock,
  Calendar,
  BarChart3,
  Shield,
  Smartphone,
  Globe,
  Zap,
  Eye,
  MessageSquare,
  FileText,
  Ticket,
  CreditCard,
  TrendingUp,
  Lock,
  Database,
  Network,
  CheckCircle,
  Target,
  Activity,
  Rocket,
  Bell,
  Settings,
  Fuel,
  Wrench,
  UserCheck,
  Route,
  Wifi,
  Star,
  Leaf,
  AlertTriangle,
  Video,
  Headphones,
  LayoutDashboard,
  Workflow,
  PieChart,
  BadgePoundSterling,
  Gauge,
  Timer,
  Map,
  Layers
} from 'lucide-react';
import type { ProductFeatureConfig } from './types';

export const passengerTransportationManagementConfig: ProductFeatureConfig = {
  keyBenefits: [
    {
      icon: Navigation,
      title: "AI-Powered Route Optimization & Scheduling",
      description: "Intelligent algorithms that optimize routes in real-time based on traffic, demand patterns, and operational constraints to maximize efficiency and punctuality.",
      metrics: "30% efficiency improvement",
      color: "from-emerald-500 to-teal-600"
    },
    {
      icon: MapPin,
      title: "Real-Time Fleet Tracking & Telematics",
      description: "GPS-powered vehicle tracking with live location updates, speed monitoring, geofencing, and comprehensive telematics data for complete fleet visibility.",
      metrics: "Sub-second location accuracy",
      color: "from-blue-500 to-indigo-600"
    },
    {
      icon: Users,
      title: "Passenger Information & Experience Management",
      description: "Real-time passenger information systems with journey planning, delay notifications, and multi-channel communication for exceptional customer experience.",
      metrics: "40% higher satisfaction scores",
      color: "from-purple-500 to-pink-600"
    },
    {
      icon: Ticket,
      title: "Multi-Channel Ticketing & E-Payments",
      description: "Complete ticketing solution with mobile apps, contactless payments, QR codes, and integration with national payment schemes and transport cards.",
      metrics: "95% payment success rate",
      color: "from-amber-500 to-orange-600"
    },
    {
      icon: Wrench,
      title: "Predictive Maintenance & Vehicle Health",
      description: "AI-driven predictive maintenance that monitors vehicle health, predicts failures, and schedules maintenance to prevent breakdowns and extend asset life.",
      metrics: "45% reduction in breakdowns",
      color: "from-red-500 to-rose-600"
    },
    {
      icon: BarChart3,
      title: "Comprehensive Analytics & Business Intelligence",
      description: "Real-time dashboards with demand forecasting, route profitability analysis, passenger flow analytics, and AI-powered operational insights.",
      metrics: "Real-time decision support",
      color: "from-cyan-500 to-blue-600"
    },
    {
      icon: Calendar,
      title: "Intelligent Crew Rostering & Management",
      description: "Automated crew scheduling with fatigue management, certification tracking, shift optimization, and compliance with working time regulations.",
      metrics: "25% labor cost reduction",
      color: "from-indigo-500 to-purple-600"
    },
    {
      icon: Fuel,
      title: "Fuel Management & Sustainability Tracking",
      description: "Comprehensive fuel consumption monitoring, eco-driving analytics, carbon footprint tracking, and sustainability reporting for green fleet management.",
      metrics: "20% fuel cost savings",
      color: "from-green-500 to-emerald-600"
    },
    {
      icon: Bell,
      title: "Real-Time Disruption Management",
      description: "Intelligent service disruption handling with automated passenger notifications, recovery planning, and alternative routing suggestions.",
      metrics: "50% faster recovery times",
      color: "from-rose-500 to-red-600"
    },
    {
      icon: BadgePoundSterling,
      title: "Revenue Management & Dynamic Pricing",
      description: "Advanced yield management with dynamic pricing, demand-based fare adjustments, and comprehensive revenue analytics for maximum profitability.",
      metrics: "15% revenue increase",
      color: "from-violet-500 to-purple-600"
    },
    {
      icon: Layers,
      title: "Multi-Modal Transport Integration",
      description: "Seamless integration across bus, rail, ferry, and coach services with unified scheduling, ticketing, and passenger journey management.",
      metrics: "Single platform for all modes",
      color: "from-teal-500 to-cyan-600"
    },
    {
      icon: Star,
      title: "Customer Loyalty & Rewards Platform",
      description: "Built-in loyalty program management with points accumulation, rewards redemption, personalized offers, and passenger engagement analytics.",
      metrics: "35% higher retention rates",
      color: "from-yellow-500 to-amber-600"
    }
  ],
  
  advancedCapabilities: [
    {
      icon: Zap,
      title: "AI Demand Forecasting & Capacity Planning",
      description: "Machine learning algorithms that predict passenger demand patterns, optimize capacity allocation, and enable proactive service planning.",
      metrics: "92% forecast accuracy",
      color: "from-yellow-500 to-orange-600"
    },
    {
      icon: Route,
      title: "Demand-Responsive Transport (DRT) Scheduling",
      description: "Flexible routing algorithms for on-demand services with real-time booking, dynamic pickup optimization, and passenger pooling.",
      metrics: "60% higher vehicle utilization",
      color: "from-pink-500 to-rose-600"
    },
    {
      icon: Video,
      title: "CCTV Integration & Security Monitoring",
      description: "Integrated video surveillance with live streaming, incident detection, facial recognition support, and secure cloud storage.",
      metrics: "24/7 security coverage",
      color: "from-red-500 to-pink-600"
    },
    {
      icon: Wifi,
      title: "On-Board WiFi & Entertainment Management",
      description: "Manage passenger WiFi services, on-board entertainment systems, digital signage, and real-time content delivery across your fleet.",
      metrics: "Enhanced passenger experience",
      color: "from-blue-500 to-cyan-600"
    },
    {
      icon: Gauge,
      title: "Driver Performance & Eco-Driving Analytics",
      description: "Monitor driver behavior with scoring systems, eco-driving training recommendations, and gamification for continuous improvement.",
      metrics: "25% improvement in driving scores",
      color: "from-emerald-500 to-green-600"
    },
    {
      icon: Timer,
      title: "Dwell Time Analysis & Stop Optimization",
      description: "AI analysis of boarding/alighting patterns, stop dwell times, and passenger flow to optimize timetables and improve punctuality.",
      metrics: "15% improved punctuality",
      color: "from-indigo-500 to-violet-600"
    },
    {
      icon: Map,
      title: "Interactive Journey Planning Portal",
      description: "Customer-facing journey planner with real-time updates, multi-modal routing, accessibility options, and integration with popular mapping apps.",
      metrics: "Seamless passenger journeys",
      color: "from-purple-500 to-indigo-600"
    },
    {
      icon: PieChart,
      title: "Revenue Attribution & Profitability Analysis",
      description: "Detailed route-level profitability analysis, subsidy tracking, cost allocation, and financial performance benchmarking.",
      metrics: "Complete financial visibility",
      color: "from-cyan-500 to-teal-600"
    },
    {
      icon: Leaf,
      title: "Carbon Footprint & ESG Reporting",
      description: "Comprehensive sustainability tracking with CO2 emissions calculation, ESG reporting, and progress towards net-zero targets.",
      metrics: "Full environmental reporting",
      color: "from-green-500 to-lime-600"
    },
    {
      icon: LayoutDashboard,
      title: "Custom Dashboard & Reporting Builder",
      description: "Create personalized operational dashboards with drag-and-drop widgets, custom KPIs, and automated report scheduling.",
      metrics: "Unlimited customization",
      color: "from-violet-500 to-purple-600"
    },
    {
      icon: Workflow,
      title: "Workflow Automation & Alerts Engine",
      description: "Build custom automation rules for maintenance alerts, delay notifications, compliance reminders, and operational triggers.",
      metrics: "500+ automation triggers",
      color: "from-orange-500 to-red-600"
    },
    {
      icon: Globe,
      title: "API Ecosystem & Third-Party Integration",
      description: "Open API platform for integration with traffic systems, payment gateways, government portals, and third-party transport apps.",
      metrics: "100+ API endpoints",
      color: "from-teal-500 to-blue-600"
    }
  ],
  
  securityPerformance: [
    {
      icon: Target,
      title: 'SOC 2 & ISO 27001 Compliance',
      description: 'Full compliance with international security standards including SOC 2 Type II, ISO 27001, and transport-specific regulations (DVSA, ORR)',
      metrics: 'Complete regulatory compliance',
      color: 'from-indigo-500 to-purple-600'
    },
    {
      icon: Lock,
      title: 'End-to-End Data Encryption',
      description: 'Military-grade encryption for all data in transit and at rest, secure API communications, and encrypted backup storage',
      metrics: 'AES-256 encryption',
      color: 'from-green-500 to-emerald-600'
    },
    {
      icon: Rocket,
      title: 'Scalable Cloud Infrastructure',
      description: 'Enterprise-grade cloud platform with auto-scaling, multi-region deployment, and 99.99% uptime SLA for mission-critical operations',
      metrics: '99.99% uptime guaranteed',
      color: 'from-blue-500 to-indigo-600'
    },
    {
      icon: Shield,
      title: 'PCI DSS Payment Security',
      description: 'Full PCI DSS compliance for secure payment processing, tokenized card storage, and fraud detection systems',
      metrics: 'Level 1 PCI compliance',
      color: 'from-red-500 to-pink-600'
    },
    {
      icon: Eye,
      title: 'Multi-Factor Authentication & SSO',
      description: 'Enterprise SSO integration with biometric authentication, hardware tokens, and risk-based access controls',
      metrics: 'Zero-trust access control',
      color: 'from-purple-500 to-indigo-600'
    },
    {
      icon: Activity,
      title: 'Comprehensive Audit Trails',
      description: 'Complete logging of all user actions, system changes, and operational events for compliance and security monitoring',
      metrics: 'Complete audit visibility',
      color: 'from-yellow-500 to-amber-600'
    },
    {
      icon: Database,
      title: 'Disaster Recovery & Business Continuity',
      description: 'Automated backups with geo-redundant storage, point-in-time recovery, and tested disaster recovery procedures',
      metrics: 'Zero data loss guarantee',
      color: 'from-emerald-500 to-green-600'
    },
    {
      icon: Network,
      title: 'Real-Time Security Monitoring',
      description: 'Continuous security monitoring, intrusion detection, automated threat response, and 24/7 SOC coverage',
      metrics: 'Real-time threat detection',
      color: 'from-orange-500 to-red-600'
    },
    {
      icon: CheckCircle,
      title: 'GDPR & Data Protection Compliance',
      description: 'Full GDPR compliance with passenger data protection, consent management, and data subject rights handling',
      metrics: 'Complete privacy compliance',
      color: 'from-cyan-500 to-blue-600'
    }
  ]
};
